import { useMemo } from 'react';
import { useFilteredSecurities } from './useFilteredSecurities';

export function useUniverseStats() {
  const {
    securities,
    universeSize,
    universeMarketCap,
    universeCountries,
    isLoading,
    error,
    securitiesStatus,
  } = useFilteredSecurities();

  const stats = useMemo(() => {
    // NOTE: these zeros are only meaningful when `status === 'ready'` (a real,
    // empty filter result). Callers MUST consult the status before rendering
    // them — otherwise a ClickHouse outage reads as "this index has 0
    // constituents and $0 market cap".
    if (securities.length === 0) {
      return {
        countries: 0,
        countriesInitial: universeCountries.size,
        constituents: 0,
        universeSize,
        marketCap: 0,
        universeMarketCap: universeMarketCap / 1_000_000_000_000,
      };
    }

    const countrySet = new Set<string>();
    let totalMarketCap = 0;

    for (const s of securities) {
      countrySet.add(s.country);
      totalMarketCap += s.marketCap;
    }

    return {
      countries: countrySet.size,
      // Layer-1 distinct-country count — the "N / initial" denominator, shown
      // only when a whole country drops out of the filtered set.
      countriesInitial: universeCountries.size,
      constituents: securities.length,
      // Names in the selected universe before any wizard-side filter
      // (scope, size, type, GICS, liquidity). UniversePreview shows
      // "constituents / universeSize" when the two differ so users see
      // when their filter selections are narrowing the universe.
      universeSize,
      // Convert to trillions for display
      marketCap: totalMarketCap / 1_000_000_000_000,
      // Layer-1 total (trillions) — the coverage rule-of-three denominator.
      universeMarketCap: universeMarketCap / 1_000_000_000_000,
    };
  }, [securities, universeSize, universeMarketCap, universeCountries]);

  return { ...stats, isLoading, error, status: securitiesStatus };
}
